import { TCardFacet, TFacet } from '@/types/collection'
import { isSpecialRewrittenCollectionUrl } from '../constants'
import { FilterValues } from '../types/filter-store-v2.types'
import { findMatchingFacetValue } from './facet-matching'

/**
 * Centralized URL Builder Service for Collection
 * Single source of truth for all URL operations
 */
export class UrlBuilderService {
  /**
   * Detect locale prefix from pathname
   * Returns locale prefix like '/en', '/vi', or empty string for default locale
   */
  private detectLocalePrefix(pathname?: string): string {
    const path =
      pathname ||
      (typeof window !== 'undefined' ? window.location.pathname : '')
    const match = path.match(/^\/(en|vi)\//)
    return match ? `/${match[1]}` : ''
  }

  /**
   * Detect base path from current pathname or provided pathname
   * Includes locale prefix if present (e.g., '/en/collection' or '/collection')
   */
  private detectBasePath(pathname?: string): string {
    const localePrefix = this.detectLocalePrefix(pathname)
    const basePath = '/collection'
    return `${localePrefix}${basePath}`
  }

  /**
   * Build collection URL with filters and concat value
   * Handles multi-value concat facets: first value in slug, rest in query params
   */
  buildCollectionUrl(params: {
    originalSlug: string
    filters: FilterValues
    concatValue?: string | null
    basePath?: string
    currentPathname?: string
    facets?: TFacet[]
    cardFacets?: TCardFacet[]
    preserveParams?: string[]
  }): string {
    const {
      originalSlug,
      filters,
      concatValue,
      basePath,
      currentPathname,
      facets,
      cardFacets,
      preserveParams = [],
    } = params

    const detectedBasePath = basePath || this.detectBasePath(currentPathname)
    let newSlug = originalSlug
    const urlParams = new URLSearchParams()

    // Extract preserved params from current URL before processing filters
    const preservedParams: Record<string, string> = {}
    if (preserveParams.length > 0) {
      let currentUrl: URL | null = null
      if (typeof window !== 'undefined') {
        // Client-side: use window.location.href which includes query params
        try {
          currentUrl = new URL(window.location.href)
        } catch {
          // Ignore URL parsing errors
        }
      } else if (currentPathname) {
        // SSR: currentPathname might include query params
        // Try to construct URL from currentPathname (may include query string)
        try {
          // Construct URL from currentPathname (with or without query params)
          currentUrl = new URL(currentPathname, 'http://localhost')
        } catch {
          // Ignore URL parsing errors
        }
      }

      if (currentUrl) {
        preserveParams.forEach((param) => {
          const value = currentUrl!.searchParams.get(param)
          if (value) {
            preservedParams[param] = value
          }
        })
      }
    }

    // Find which filter key corresponds to the concat value (if any)
    // Prioritize 'color' facet over others
    // IMPORTANT: Lock the FIRST value in filters as concatValue
    let concatFilterKey: string | null = null
    let lockedConcatValue: string | null = concatValue || null

    if (facets || cardFacets) {
      const allFacets = [...(facets || []), ...(cardFacets || [])]
      const concatFacets = allFacets.filter((f) => f?.is_concat_slug)

      // Deduplicate by seo_alias
      const seenAliases = new Set<string>()
      const uniqueFacets = concatFacets.filter((f) => {
        if (seenAliases.has(f.seo_alias)) {
          return false
        }
        seenAliases.add(f.seo_alias)
        return true
      })

      // Sort by priority: color first
      const CONCAT_FACET_PRIORITY = ['color', 'mau-sac']
      const sortedFacets = uniqueFacets.sort((a, b) => {
        const priorityA = CONCAT_FACET_PRIORITY.indexOf(a.seo_alias)
        const priorityB = CONCAT_FACET_PRIORITY.indexOf(b.seo_alias)
        if (priorityA >= 0 && priorityB >= 0) return priorityA - priorityB
        if (priorityA >= 0) return -1
        if (priorityB >= 0) return 1
        return 0
      })

      // Find the first concat facet with values in filters
      // Lock the FIRST value as concatValue ONLY if:
      // - Single value OR
      // - Multiple values but facet does NOT allow multiple

      for (const facet of sortedFacets) {
        const filterValue = filters[facet.seo_alias]
        if (filterValue) {
          const values = Array.isArray(filterValue)
            ? filterValue
            : [String(filterValue)]

          const firstValue = values[0]

          // Check if facet allows multiple values
          const isAllowMultiple = facet.is_allow_multiple || false

          if (values.length > 0) {
            concatFilterKey = facet.seo_alias

            // Only lock concat value if:
            // - Single value (length === 1) OR
            // - Multiple values but facet does NOT allow multiple
            if (values.length === 1 || !isAllowMultiple) {
              lockedConcatValue = firstValue
            } else {
              // Multiple values AND facet allows multiple: NO concat in slug
              lockedConcatValue = null
            }

            break
          }
        }
      }
      // if (!concatFilterKey) {
      //   logger.warn('⚠️ [buildCollectionUrl] concatFilterKey NOT found!')
      // }
    }

    // Handle concat value - SUPPORT BOTH hardcode and template pages
    // Use lockedConcatValue (first value from filters) if available, otherwise use concatValue
    const finalConcatValue = lockedConcatValue || concatValue

    // Check if current pathname or originalSlug should prevent concat
    // Special rewritten URLs and certain collection slugs should NOT concat slug, only use query params
    // For special rewritten collections, preserve the entire pathname (including locale if present)
    const pathnameOrSlug = currentPathname || originalSlug
    const shouldPreventConcat = isSpecialRewrittenCollectionUrl(pathnameOrSlug)

    if (
      finalConcatValue &&
      finalConcatValue !== null &&
      finalConcatValue !== '' &&
      !shouldPreventConcat
    ) {
      // Append concat value to slug for regular collections (hardcode + template)
      // BUT NOT for special rewritten collections
      newSlug = `${originalSlug}-${finalConcatValue}`
    }

    // Process all filters
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'page' && Number(value) === 1) {
        return // Skip page=1
      }

      if (value === undefined || value === null || value === '') {
        return
      }

      // Always add page parameter if it exists and > 1
      if (key === 'page' && Number(value) > 1) {
        urlParams.set(key, String(value))
        return
      }

      // Special handling for concat filter with multiple values
      if (concatFilterKey && key === concatFilterKey) {
        const values = Array.isArray(value) ? value : [String(value)]

        // Find the facet to check is_allow_multiple
        const allFacets = [...(facets || []), ...(cardFacets || [])]
        const currentFacet = allFacets.find((f) => f?.seo_alias === key)
        const isAllowMultiple = currentFacet?.is_allow_multiple || false

        if (values.length === 1) {
          // Single value: if preventConcat is true, add to params; otherwise already in slug
          if (shouldPreventConcat) {
            // For special rewritten collections, add to query params instead of slug
            // PHỤC vụ các collection được REWRITES vd: 'sieu-sale-ngay-doi-double-day'
            urlParams.set(key, values[0])
          }
          return
        } else if (values.length > 1) {
          // Multiple values for the SAME facet
          // If facet allows multiple: ALL values go to query params, NO concat in slug
          if (isAllowMultiple) {
            urlParams.set(key, values.join(','))
          } else {
            // Facet doesn't allow multiple: only first value in slug, rest in params
            // const firstValue = values[0]
            const remainingValues = values.slice(1)

            // if (finalConcatValue !== firstValue) {
            //   logger.warn(
            //     `⚠️ [buildCollectionUrl] ConcatValue mismatch! Expected first value "${firstValue}", got "${finalConcatValue}". First value will be locked.`
            //   )
            // }

            if (remainingValues.length > 0) {
              urlParams.set(key, remainingValues.join(','))
            }
          }
          return
        }
      }

      // Add to query params for regular filters
      if (Array.isArray(value)) {
        if (value.length > 0) {
          urlParams.set(key, value.join(','))
        }
      } else {
        urlParams.set(key, String(value))
      }
    })

    // Restore preserved params after processing all filters
    // This ensures preserved params are added after filter params
    // Skip preserved params that are already in filters (filters take precedence)
    Object.entries(preservedParams).forEach(([key, value]) => {
      // Don't overwrite if the param is already in filters (filters take precedence)
      if (
        !(key in filters) ||
        filters[key] === undefined ||
        filters[key] === null ||
        filters[key] === ''
      ) {
        urlParams.set(key, value)
      }
    })

    const queryString = urlParams.toString()

    // Special rewritten collections: preserve original pathname instead of building from basePath + slug
    // MUST check shouldPreventConcat FIRST before building regular URL
    if (shouldPreventConcat) {
      // If currentPathname is provided, use it
      // Otherwise, try to build from original pathname pattern
      if (currentPathname) {
        // Preserve the original pathname (without existing query params)
        const pathnameWithoutQuery = currentPathname.split('?')[0]
        const finalUrl = `${pathnameWithoutQuery}${queryString ? `?${queryString}` : ''}`

        return finalUrl
      } else {
        // If no currentPathname but should prevent concat, build from original slug
        // This happens in server-side rendering
        // Use originalSlug if it's in SPECIAL_REWRITTEN_COLLECTION_URLS, otherwise use basePath + originalSlug
        const localePrefix = this.detectLocalePrefix()
        const finalUrl = `${detectedBasePath}/${originalSlug}${queryString ? `?${queryString}` : ''}`

        return finalUrl
      }
    }

    // Regular collections: build from basePath + slug
    const finalUrl = `${detectedBasePath}/${newSlug}${queryString ? `?${queryString}` : ''}`
    return finalUrl
  }

  /**
   * Parse URL to extract filters and concat value
   * Merges values from both slug and query params for the same facet
   */
  parseUrl(
    url: URL,
    originalSlug: string,
    facets?: TFacet[],
    cardFacets?: TCardFacet[]
  ): {
    filters: Record<string, string | string[]>
    concatValue: string | null
  } {
    const filters: Record<string, string | string[]> = {}
    let concatValue: string | null = null
    let concatFacetKey: string | null = null

    // Check if this is a special rewritten collection URL
    // Special rewritten URLs should NOT parse concat from pathname
    const isSpecialRewritten = isSpecialRewrittenCollectionUrl(url.pathname)

    // Parse slug-concatenated filters FIRST
    // Handle optional locale prefix (e.g., /en/collection/ or /collection/)
    const pathSlug = url.pathname.replace(/^\/(en|vi)?\/?(collection)\//, '')
    if (!isSpecialRewritten && pathSlug && pathSlug !== originalSlug) {
      // Extract filter values from slug
      const slugParts = pathSlug.split('-')
      const originalParts = originalSlug?.split('-') || []

      // Find the difference (new filter values)
      const newParts = slugParts.slice(originalParts.length)
      if (newParts.length > 0) {
        const rawConcatValue = newParts.join('-')

        // Map concat value to actual facet keys if facets data is available
        if (facets || cardFacets) {
          const allFacets = [...(facets || []), ...(cardFacets || [])]
          let mapped = false

          // Try to find exact matching facet value
          for (const facet of allFacets) {
            if (!facet?.is_concat_slug) continue

            const matchingValue = facet.facet_values?.find(
              (value) => value.seo_alias === rawConcatValue
            )

            if (matchingValue) {
              // Store as array to prepare for merging with query params
              filters[facet.seo_alias] = [matchingValue.seo_alias]
              concatValue = matchingValue.seo_alias
              concatFacetKey = facet.seo_alias
              mapped = true
              break
            }
          }

          // If no exact match found, try partial matching
          if (!mapped) {
            const match = findMatchingFacetValue(
              rawConcatValue,
              facets || [],
              cardFacets || []
            )
            if (match) {
              // Store as array to prepare for merging with query params
              filters[match.facet.seo_alias] = [match.value.seo_alias]
              concatValue = match.value.seo_alias
              concatFacetKey = match.facet.seo_alias
              mapped = true
            }
          }

          // If no mapping found, keep as slug_concat for fallback
          if (!mapped) {
            filters['slug_concat'] = rawConcatValue
            concatValue = rawConcatValue
          }
        } else {
          // Fallback: keep as slug_concat if no facets data
          filters['slug_concat'] = rawConcatValue
          concatValue = rawConcatValue
        }
      }
    }

    // Parse query parameters and MERGE with slug values
    url.searchParams.forEach((value, key) => {
      if (key === 'limit') return

      // Handle multiple values (comma-separated)
      let paramValues: string[]
      if (value.includes(',')) {
        paramValues = value.split(',')
      } else {
        paramValues = [value]
      }

      // If this key already has a value from slug (concat value), merge them
      if (key === concatFacetKey && filters[key]) {
        const existingValues = Array.isArray(filters[key])
          ? (filters[key] as string[])
          : [filters[key] as string]

        // Merge and deduplicate
        const mergedValues = Array.from(
          new Set([...existingValues, ...paramValues])
        )
        filters[key] = mergedValues

        // If we have multiple values after merge, check if facet allows multiple
        // If yes, clear concatValue (no concat in slug for multiple values)
        if (mergedValues.length > 1 && (facets || cardFacets)) {
          const allFacets = [...(facets || []), ...(cardFacets || [])]
          const currentFacet = allFacets.find((f) => f?.seo_alias === key)
          if (currentFacet?.is_allow_multiple) {
            concatValue = null
          }
        }
      } else {
        // New key, just store the values
        filters[key] = paramValues.length === 1 ? paramValues[0] : paramValues
      }
    })

    return { filters, concatValue }
  }

  /**
   * Update browser URL using history API
   * Uses replaceState to avoid creating new history entries for filter changes
   */
  updateBrowserUrl(url: string): void {
    if (typeof window === 'undefined') return
    window.history.replaceState(null, '', url)
  }

  /**
   * Navigate to new URL (creates new history entry)
   * Use for actual navigation, not filter changes
   */
  navigateToUrl(url: string): void {
    if (typeof window === 'undefined') return
    window.history.pushState(null, '', url)
  }

  /**
   * Get current URL as URL object
   */
  getCurrentUrl(): URL {
    if (typeof window === 'undefined') {
      throw new Error('getCurrentUrl can only be called in browser environment')
    }
    return new URL(window.location.href)
  }

  /**
   * Clean URL by removing page=1 and empty params
   */
  cleanUrl(url: URL): string {
    // Remove page=1
    if (url.searchParams.get('page') === '1') {
      url.searchParams.delete('page')
    }

    return url.pathname + (url.search ? url.search : '')
  }

  /**
   * Extract concat value from URL pathname
   */
  extractConcatValueFromUrl(
    pathname: string,
    originalSlug: string
  ): string | null {
    // Handle optional locale prefix (e.g., /en/collection/ or /collection/)
    const pathSlug = pathname.replace(/^\/(en|vi)?\/?(collection)\//, '')
    if (!pathSlug || pathSlug === originalSlug) {
      return null
    }

    const slugParts = pathSlug.split('-')
    const originalParts = originalSlug.split('-')

    // Find the difference (new filter values)
    const newParts = slugParts.slice(originalParts.length)
    if (newParts.length > 0) {
      // Return full remaining parts joined with hyphen
      return newParts.join('-')
    }

    return null
  }

  /**
   * Check if URL already has concatenated values
   */
  hasConcatValue(
    pathname: string,
    originalSlug: string,
    basePath: string
  ): boolean {
    const fullBasePath = `${basePath}/${originalSlug}`

    if (!pathname.startsWith(fullBasePath)) {
      return false
    }

    const remainingPath = pathname.slice(fullBasePath.length)
    if (!remainingPath || remainingPath === '/') {
      return false
    }

    // Check if there's anything after the original slug
    const concatPart = remainingPath.replace(/^\//, '')
    const originalParts = originalSlug.split('-')
    const pathParts = concatPart.split('-')

    return pathParts.length > originalParts.length
  }
}

// Export singleton instance
export const urlBuilder = new UrlBuilderService()
